<template>
  <div class="bz-selector-page">
    <upsell-modal v-if="modalOpen" :product="product" :selection="selection" :offered-bands="upsellBands"
      @accept="onUpsellAccept" @decline="onUpsellDecline" />

    <div class="bz-pagehead">
      <div class="bz-pagehead-l">
        <h1>{{ title }}</h1>
        <beta-pill :product="productKey" />
      </div>
    </div>

    <div class="bzB rail">
      <div class="bzB-steps">
        <step-card :n="1" title="Industry" icon="factory" :is-open="open === 1" @toggle="toggle(1)"
          :value="selection.industry && selection.industry.name + ' [' + selection.industry.code + ']'">
          <industry-step :product="product" :selected="selection.industry" :favorites="favorites"
            :show-class-counts="product === 'IFP'"
            @select="onIndustrySelect" @toggle-fav="toggleFav" />
        </step-card>

        <step-card :n="2" title="Market Area" icon="public" :is-open="open === 2" @toggle="toggle(2)"
          :locked="solo" lock-note="All US (Sole Proprietorship)"
          :value="selection.area && selection.area.label">
          <area-step v-if="selection.industry" :product="product" :industry="selection.industry"
            :value="selection.area" @change="onAreaChange" />
          <div v-else class="bz-empty">Pick an industry first — each area then shows whether it has data for it.</div>
        </step-card>

        <step-card v-if="product === 'IFP'" :n="3" title="Sales Class" icon="paid" :is-open="open === 3" @toggle="toggle(3)"
          :locked="solo" lock-note="Industrywide (Sole Proprietorship)"
          :value="selection.sc && salesLabel(selection.sc)">
          <sales-class-step v-if="selection.industry" :industry="selection.industry" :value="selection.sc"
            @change="onScChange" />
          <div v-else class="bz-empty">Pick an industry first — offered classes depend on it.</div>
        </step-card>

        <step-card v-if="product === 'IFP'" :n="4" title="Structure &amp; Time Series" icon="tune" preset
          :is-open="open === 4" @toggle="toggle(4)"
          :value="structureLabel + ' · ' + timelineLabel">
          <div class="bz-defaults wide">
            <label>Structure</label>
            <div class="bz-seg">
              <button type="button" :class="{ on: selection.structure === 'industry_firms' }"
                @click="setStructure('industry_firms')">Industry Firms</button>
              <!-- SP hidden until 3_26 MF/SPIF data exists — see helpers/spifAvailability.js -->
              <button v-if="spSelectorEnabled" type="button" :class="{ on: selection.structure === 'sole_proprietorship' }"
                @click="setStructure('sole_proprietorship')">Sole Proprietorship</button>
            </div>
            <label>Time Series</label>
            <div class="bz-seg">
              <button type="button" :class="{ on: selection.timeline === '3yr' }" @click="setTimeline('3yr')">3 Years Profile</button>
              <button type="button" :class="{ on: selection.timeline === '5yr' }" @click="setTimeline('5yr')">5 Years Profile</button>
            </div>
          </div>
          <div v-if="solo" class="bz-constraint">
            <i class="material-icons" style="font-size:14px;line-height:1;">info</i>
            Sole Proprietorship locks Market Area to All US and Sales Class to Industrywide.
          </div>
        </step-card>
      </div>

      <summary-rail :product="product" :selection="selection" :status="status"
        :cta-done="ctaDone" :submitting="submitting"
        :upsell-accepted="upsellAccepted" :upsell-band="upsellBand"
        :legacy-available="legacy.available" :legacy-label="legacyLabel" :legacy-checked="legacy.checked"
        @legacy-toggle="legacy.checked = $event"
        @jump="toggle" @access="handleAccess" />
    </div>
  </div>
</template>

<script>
import StepCard from './StepCard.vue';
import BetaPill from './BetaPill.vue';
import IndustryStep from './IndustryStep.vue';
import AreaStep from './AreaStep.vue';
import SalesClassStep from './SalesClassStep.vue';
import SummaryRail from './SummaryRail.vue';
import UpsellModal from './UpsellModal.vue';
import {
  SALES, SALES_BY_ID, fetchOfferedSalesClasses, areaCodeOf,
  createIfSearchProfile, createImrSearchProfile, queueLoad, withTimeout,
  fetchIfAvailability, fetchIfLegacyAvailability, fetchImrSelectionAvailability,
} from './model';
import {
  trackSelectorViewed, trackIndustrySelected, trackStepCompleted, trackSelectorCompleted,
  trackUpsellShown, trackUpsellAccepted, trackUpsellDismissed,
} from './analytics';
import './beta-selector.css';
import { SP_SELECTOR_ENABLED } from '@/helpers/spifAvailability';

export default {
  name: 'selector-page',
  components: { StepCard, BetaPill, IndustryStep, AreaStep, SalesClassStep, SummaryRail, UpsellModal },
  props: {
    product: { type: String, required: true }, // 'IFP' | 'IMP'
  },
  data() {
    return {
      spSelectorEnabled: SP_SELECTOR_ENABLED,
      selection: {
        industry: null, // {code, name, state, msg}
        area: null, // {kind, stateId?, metroId?, radius?, label}
        sc: null,
        structure: 'industry_firms',
        timeline: '5yr',
      },
      favorites: [],
      open: 1,
      ctaDone: false,
      submitting: false,
      modalOpen: false,
      upsellAccepted: false,
      upsellDismissed: false,
      upsellBand: null,
      upsellBands: [], // offered bands for the IMP→IFP collect step
      // Pre-generation IF data availability for the current IFP selection:
      // null = unknown/checking, 'ok' = data present, 'nodata' = empty segment,
      // 'notoffered' = not offered at this granularity. IMP never sets this.
      availability: null,
      probeSeq: 0, // drop-stale guard: only the newest probe may set availability
      // Pre-purchase IMP data availability for the current selection: null =
      // unknown/checking, 'ok' = data present (or the probe couldn't tell --
      // fails open), 'nodata' = confirmed zero. IFP never sets this. See
      // probeImpAvailability() -- the state/metro area picker already excludes
      // no-data areas server-side, but ZIP and county browse geography with no
      // industry filtering at all, so this is the only gate that actually
      // covers a zip/county/radius pick before purchase (found 2026-09-21 via
      // a real customer report: profile 18810991, a decimal industry code with
      // zero real businesses in the one zip code purchased -- nothing had ever
      // checked).
      impAvailability: null,
      impProbeSeq: 0,
      // Legacy (archive-year) add-on — offered for IFP + All-US, same as the
      // classic selector's "Add legacy year(s)" checkbox (the beta used to
      // hardcode access_legacy:false and never offered it).
      legacy: { available: false, yearsRange: '', price: null, checked: false },
      legacySeq: 0,
    };
  },
  computed: {
    productKey() {
      return this.product === 'IMP' ? 'imp' : 'ifp';
    },
    title() {
      return this.product === 'IFP' ? 'Industry Financial Profile' : 'Industry Market Profile';
    },
    solo() {
      return this.product === 'IFP' && this.selection.structure === 'sole_proprietorship';
    },
    structureLabel() {
      return this.selection.structure === 'sole_proprietorship' ? 'Sole Proprietorship' : 'Industry Firms';
    },
    timelineLabel() {
      return this.selection.timeline === '3yr' ? '3 Years Profile' : '5 Years Profile';
    },
    legacyLabel() {
      if (!this.legacy.available) return '';
      const base = 'Add legacy years ' + this.legacy.yearsRange;
      // Classic shows the price only to non-subscribers (the add-on is included
      // for usif_p/lif_p accounts); the cart itemizes it either way.
      // user_subscriptions is only an array for subscriber/retail sessions. Academic sessions
      // never populate it (the store default is `{}`, which is truthy and has no .indexOf), and
      // calling .indexOf on it throws inside this computed -- which runs in every render once the
      // legacy add-on is offered (All US) -- so Vue stops patching the DOM and the rail stays frozen
      // on "Checking data availability…" with Access Now disabled. Academic coverage includes
      // usif_p/lif_p (API GetAcademicCoverage) and accessProduct books the add-on as paid for it,
      // so treat academic as subscribed.
      const user = this.$store.state.user;
      const subs = Array.isArray(user.user_subscriptions) ? user.user_subscriptions : [];
      const academic = !!(user.user_type && user.user_type.academic);
      const subscribed = academic || subs.indexOf('usif_p') !== -1 || subs.indexOf('lif_p') !== -1;
      return !subscribed && this.legacy.price != null ? base + ' · $' + this.legacy.price : base;
    },
    status() {
      const s = this.selection;
      if (!s.industry) return { state: 'todo', msg: 'Select an industry to see what’s available' };
      if (!s.area) return { state: 'todo', msg: 'Select a market area — only areas with data are enabled' };
      if (this.product === 'IFP' && !s.sc) return { state: 'todo', msg: 'Select a sales class — only offered classes are enabled' };
      // IFP only: block generation before the user pays when the exact segment has no
      // data. /info/sales_classes lists offered bands, but a band can be offered yet empty
      // (e.g. a decimal NAICS whose sc2 was zeroed in the 3_26 rebuild) — the report would
      // then fail to a "no data" panel AFTER purchase. availability is set by an async probe
      // on selection changes; fail-open ('ok') if the probe errors so we never wrongly block.
      if (this.product === 'IFP' && s.sc) {
        if (this.availability === null) return { state: 'todo', msg: 'Checking data availability…' };
        if (this.availability === 'nodata') {
          return { state: 'blocked', msg: 'This release has no industry financial data for the selected industry, market area and sales class. Try a different sales class, or a broader industry or market.' };
        }
        if (this.availability === 'notoffered') {
          return { state: 'blocked', msg: 'Financial data isn’t offered at this level of detail for this combination. Try a broader industry or the All-US market.' };
        }
      }
      // IMP: same idea as the IFP block above, but simpler (no sales class) and covers
      // MORE ground -- the standard-tab area picker already excludes no-data states/
      // metros server-side, but the ZIP and county tabs browse industry-independent
      // geography with no filtering, so this probe is the only check a zip/county/radius
      // pick ever gets. See probeImpAvailability(). Fail-open ('ok') on a probe error,
      // same policy as IFP -- a transient check failure must never cost a real sale.
      if (this.product === 'IMP') {
        if (this.impAvailability === null) return { state: 'todo', msg: 'Checking data availability…' };
        if (this.impAvailability === 'nodata') {
          return { state: 'blocked', msg: 'This release has no market data for the selected industry and market area. Try a broader industry or a wider market area.' };
        }
      }
      return { state: 'ok' };
    },
  },
  created() {
    this.loadFavorites();
  },
  mounted() {
    trackSelectorViewed(this.productKey, 'beta');
  },
  methods: {
    salesLabel(id) {
      return SALES_BY_ID[id] ? SALES_BY_ID[id].label : id;
    },
    toggle(n) {
      this.open = this.open === n ? 0 : n;
    },
    freshen() {
      this.ctaDone = false;
      this.upsellAccepted = false;
      this.upsellDismissed = false;
      this.upsellBand = null;
    },
    // Probe IF data availability for the current IFP selection (see status()). IFP only —
    // IMP never sets a sales class and has no per-segment IF gate. Fails open on error/timeout
    // and drops stale responses so rapid re-picking can't leave a wrong verdict on screen.
    async probeAvailability() {
      if (this.product !== 'IFP') return;
      const s = this.selection;
      if (!s.industry || !s.area || !s.sc) { this.availability = null; return; }
      const areaCode = areaCodeOf(s.area);
      if (areaCode == null) { this.availability = 'ok'; return; } // radius/zips: no single-area check
      const seq = ++this.probeSeq;
      this.availability = null; // 'checking…'
      try {
        const data = await withTimeout(
          fetchIfAvailability({ industryCode: s.industry.code, areaCode, salesClassId: s.sc, soleProp: this.solo }),
          8000, 'if_available',
        );
        if (seq !== this.probeSeq) return; // a newer selection superseded this probe
        if (data === 0) this.availability = 'nodata';
        else if (data === -1) this.availability = 'notoffered';
        else this.availability = 'ok';
      } catch (e) {
        if (seq !== this.probeSeq) return;
        this.availability = 'ok'; // fail open — never block on a probe failure
      }
    },
    // Probe IMP data availability for the current selection (see status()). IMP only --
    // mirrors probeAvailability() above but with no sales-class gate (IMP never sets
    // one) and it's the ONLY availability check IMP gets for a zip/county/radius pick
    // (the standard-tab state/metro list is already filtered server-side; ZIP/county
    // browse industry-independent geography with no filtering at all). Fails open on
    // error/timeout and drops stale responses, same policy as the IFP probe.
    async probeImpAvailability() {
      if (this.product !== 'IMP') return;
      const s = this.selection;
      if (!s.industry || !s.area) { this.impAvailability = null; return; }
      const seq = ++this.impProbeSeq;
      this.impAvailability = null; // 'checking…'
      try {
        const res = await withTimeout(
          fetchImrSelectionAvailability({ industryCode: s.industry.code, area: s.area }),
          8000, 'imr_selection_available',
        );
        if (seq !== this.impProbeSeq) return;
        // available === false is the only confirmed-empty signal; null (kind not probe-able,
        // or an unexpected response shape) and checked === false resolve 'ok' -- fail open.
        this.impAvailability = res && res.available === false ? 'nodata' : 'ok';
      } catch (e) {
        if (seq !== this.impProbeSeq) return;
        this.impAvailability = 'ok'; // fail open — never block on a probe failure
      }
    },
    // Probe the legacy add-on for the current selection. Offered only for
    // IFP + All-US + a chosen sales class, never Sole Proprietorship (same
    // gates as classic). Fail-closed: on error the add-on just isn't offered.
    async probeLegacy() {
      this.legacy.available = false;
      this.legacy.checked = false;
      const s = this.selection;
      if (this.product !== 'IFP' || this.solo) return;
      if (!s.industry || !s.sc || !s.area || s.area.kind !== 'us') return;
      const seq = ++this.legacySeq;
      try {
        const data = await withTimeout(
          fetchIfLegacyAvailability({ industryCode: s.industry.code, salesClassId: s.sc }),
          8000, 'if_legacy_available',
        );
        if (seq !== this.legacySeq) return;
        if (data && data.available) {
          this.legacy.yearsRange = data.yearsRange || '';
          this.legacy.price = data.price != null ? data.price : null;
          this.legacy.available = true;
        }
      } catch (e) { /* fail closed */ }
    },
    onIndustrySelect({ node, method }) {
      this.selection.industry = node;
      this.selection.area = null;
      this.selection.sc = null;
      this.availability = null;
      this.probeSeq++; // invalidate any in-flight probe
      this.impAvailability = null;
      this.impProbeSeq++;
      this.legacy.available = false;
      this.legacy.checked = false;
      this.legacySeq++;
      this.open = 2;
      this.freshen();
      trackIndustrySelected(this.productKey, 'beta', method);
      this.preloadUpsellBands();
    },
    onAreaChange(area) {
      this.selection.area = area;
      this.open = this.product === 'IFP' ? 3 : 0;
      this.freshen();
      trackStepCompleted(this.productKey, 'beta', 'market_area');
      this.probeAvailability(); // re-check if a sales class is already chosen
      this.probeImpAvailability();
      this.probeLegacy();
    },
    onScChange(id) {
      this.selection.sc = id;
      this.open = 0;
      this.ctaDone = false;
      trackStepCompleted(this.productKey, 'beta', 'sales_class');
      this.probeAvailability();
      this.probeLegacy();
    },
    setStructure(v) {
      this.selection.structure = v;
      if (v === 'sole_proprietorship') {
        this.selection.area = { kind: 'us', label: 'All US' };
        this.selection.sc = '1';
      }
      this.freshen();
      trackStepCompleted(this.productKey, 'beta', 'defaults');
      this.probeAvailability(); // structure flips IF<->SPIF endpoint and may set area/sc
      this.probeLegacy();
    },
    setTimeline(v) {
      this.selection.timeline = v;
      this.freshen();
      trackStepCompleted(this.productKey, 'beta', 'defaults');
    },

    /* ---------- favorites (per account, shared across both products) ---------- */
    favKey() {
      const uid = this.$store.getters['user/user_id'] || 'anon';
      return 'bzSelectorFavs:' + uid;
    },
    loadFavorites() {
      try {
        this.favorites = JSON.parse(localStorage.getItem(this.favKey()) || '[]');
      } catch (e) {
        this.favorites = [];
      }
    },
    toggleFav(code) {
      const i = this.favorites.indexOf(code);
      if (i === -1) this.favorites.push(code);
      else this.favorites.splice(i, 1);
      localStorage.setItem(this.favKey(), JSON.stringify(this.favorites));
    },

    /* ---------- upsell ---------- */
    // Coverage check per the Brief: IMP→IFP = offered sales classes non-empty
    // AND the area has an IFP equivalent (US/state/metro — never radius).
    // IFP→IMP = superset, any standard area carries over. Preloaded (cached)
    // so Access Now never waits on it; if it never resolved, no prompt.
    async preloadUpsellBands() {
      this.upsellBands = [];
      if (!this.selection.industry) return;
      try {
        const offered = await fetchOfferedSalesClasses(this.selection.industry.code);
        this.upsellBands = SALES.filter((s) => offered.indexOf(s.id) !== -1);
      } catch (e) {
        this.upsellBands = [];
      }
    },
    upsellApplicable() {
      const a = this.selection.area;
      if (!this.selection.industry || !a) return false;
      const standardArea = a.kind === 'us' || a.kind === 'state' || a.kind === 'metro';
      if (!standardArea) return false; // radius has no IFP equivalent; IMP-add keeps it simple too
      if (this.product === 'IMP') return this.upsellBands.length > 0;
      return true; // IFP→IMP: superset, nothing to collect
    },
    onUpsellAccept(band) {
      this.upsellAccepted = true;
      this.upsellBand = band || null;
      this.modalOpen = false;
      trackUpsellAccepted(this.product === 'IMP' ? 'imp_to_ifp' : 'ifp_to_imp', band);
      this.doAccess();
    },
    onUpsellDecline() {
      this.upsellDismissed = true;
      this.modalOpen = false;
      trackUpsellDismissed(this.product === 'IMP' ? 'imp_to_ifp' : 'ifp_to_imp');
      this.doAccess();
    },

    /* ---------- access (same wiring as the classic flow) ---------- */
    handleAccess() {
      if (this.status.state !== 'ok' || this.submitting || this.ctaDone) return;
      if (this.upsellApplicable() && !this.upsellAccepted && !this.upsellDismissed) {
        this.modalOpen = true;
        trackUpsellShown(this.product === 'IMP' ? 'imp_to_ifp' : 'ifp_to_imp', 'modal');
        return;
      }
      this.doAccess();
    },
    async doAccess() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        const s = this.selection;
        const years = s.timeline === '3yr' ? 3 : 5;
        const areaCode = areaCodeOf(s.area);

        // primary product profile (every await is time-boxed — the dev API can
        // stall for minutes, and an unbounded await leaves the CTA stuck on
        // "Processing…" with no feedback)
        let profileId;
        if (this.product === 'IFP') {
          profileId = await withTimeout(createIfSearchProfile({
            industryCode: s.industry.code,
            areaCode,
            salesClassId: s.sc,
            years,
            soleProp: this.solo,
          }), 45000, 'create profile');
        } else {
          profileId = await withTimeout(createImrSearchProfile({
            industryCode: s.industry.code,
            areaCode,
            radius: s.area.kind === 'radius' ? s.area.radius : null,
            zips: s.area.kind === 'zip' ? s.area.zips : null,
          }), 45000, 'create profile');
        }

        // upsell product (added alongside; its cart/access result is not routed)
        if (this.upsellAccepted) {
          try {
            let upsellId;
            if (this.product === 'IMP') {
              upsellId = await withTimeout(createIfSearchProfile({
                industryCode: s.industry.code,
                areaCode,
                salesClassId: this.upsellBand,
                years: 5,
                soleProp: false,
              }), 45000, 'create upsell');
            } else {
              upsellId = await withTimeout(
                createImrSearchProfile({ industryCode: s.industry.code, areaCode, radius: null }),
                45000, 'create upsell');
            }
            await withTimeout(
              this.$store.dispatch('cart/accessProduct', { product_id: upsellId, access_legacy: false }),
              45000, 'access upsell');
          } catch (e) {
            // the upsell must never block the primary purchase
          }
        }

        await withTimeout(queueLoad(profileId, this.$store.getters['user/user_id']), 60000, 'queue load');

        const res = await withTimeout(this.$store.dispatch('cart/accessProduct', {
          product_id: profileId,
          access_legacy: this.legacy.available && this.legacy.checked,
        }), 60000, 'access product');

        if (res.cart) {
          trackSelectorCompleted(this.productKey, 'beta');
          this.ctaDone = true;
          this.$router.push({ name: 'Cart', params: { id: profileId } });
        } else if (res.access || (res.error && res.errorCode === 'product_already_accessed')) {
          trackSelectorCompleted(this.productKey, 'beta');
          this.ctaDone = true;
          if (this.product === 'IFP') {
            this.$router.push({ name: this.solo ? 'SPIFAbout' : 'IFAbout', params: { id: profileId } });
          } else {
            this.$router.push({ name: 'MarketAboutTheData', params: { id: profileId } });
          }
        } else {
          throw new Error('unexpected access response: ' + (res && res.errorCode));
        }
      } catch (e) {
        this.ctaDone = false;
        // Daily cap: the global dialog already explained it; skip the generic alert.
        if (e && e.dailyLimit) return;
        this.$store.dispatch('alerts/error', 'Something went wrong accessing this profile. Please try again.');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

